import React, { useEffect } from "react";
import {
  flexRender,
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  functionalUpdate,
} from "@tanstack/react-table";
import { Pagination } from "./Pagination/Pagination";
import Modal from "../Modal/Modal";
import { SaveIcon } from "../../../assets/icons";
import { Dropdown } from "../Dropdown/Dropdown";
import { formatPrice } from "../../../utils";

const pageSizeOptions = [
  {
    id: 10,
    name: "10개 보기",
  },
  {
    id: 20,
    name: "20개 보기",
  },
  {
    id: 50,
    name: "50개 보기",
  },
];

const Table = (props) => {
  const {
    additionalText,
    hasAdditionalText = false,
    hasAdditionalButton = false,
    hasSave = true,
    hasSort = true,
    hasPageSizeFilter = true,
    hasPagination = true,
    sorts,
    onSort = () => {},
    onSave,
    boldColumns,
    positionDropdown = "right-2",
    width = "w-20",
    dropdownWidth = "w-[150%]",
    emptyStateInTableBody,
    emptyColSpan,
    pageSize,
    totalData,
    totalPrice,
    onClickAdditionalButton,
    additionalButtonText,
    customLeftHeader,
    isRightAlignmentSort = true,
    isResettedPage,
  } = props;
  const table = useReactTable({
    data: props.data,
    columns: props.columns,
    pageCount: props.pageCount,
    state: {
      pagination: props.pagination,
    },

    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),

    onPaginationChange: (updaterOrValue) => {
      const nextState = functionalUpdate(updaterOrValue, props.pagination);

      props.onPaginationChange(nextState);
    },
    manualPagination: true,
  });

  useEffect(() => {
    if (isResettedPage) {
      handleResetPage();
    }
  }, [isResettedPage]);

  const handleResetPage = () => {
    table.setPageIndex(0);
  };

  return (
    <>
      <div className="w-full flex justify-end">
        <div
          className={`flex ${
            totalData || customLeftHeader ? "justify-between" : "justify-end"
          } items-center w-full`}
        >
          {totalData && (
            <div className="block">
              <span>
                총 <span className="font-bold">{totalData}</span>건 / 금액{" "}
                <span className="font-bold">{formatPrice(totalPrice)}원</span>
              </span>
            </div>
          )}
          {customLeftHeader && <div className="block">{customLeftHeader}</div>}
          <div className="flex gap-2">
            {hasSort && (
              <Dropdown
                textColor="text-black"
                border="border-0"
                height="h-10"
                width={width}
                positionDropdown={positionDropdown}
                dropdownWidth={dropdownWidth}
                placeholder="최신순"
                options={sorts}
                onClickItem={(id) => onSort({ orderBy: id })}
                isRightAlignment={isRightAlignmentSort}
              />
            )}
            {hasPageSizeFilter && (
              <Dropdown
                textColor="text-black"
                border="border-0"
                height="h-10"
                width="w-24"
                // placeholderColor="text-black"
                placeholder="10개 보기"
                options={pageSizeOptions}
                onClickItem={(id) => table.setPageSize(id)}
              />
            )}
          </div>
        </div>
      </div>
      <div className="block w-full whitespace-nowrap overflow-auto">
        <table className="w-full text-center">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr className="border border-[#dddddd]" key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      className={`p-2 bg-[#f2f2f4] border border-[#dddddd] ${
                        boldColumns?.includes(header?.column?.id)
                          ? "border-r-2 border-r-[#cccccc]"
                          : ""
                      }`}
                      key={header.id}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          {emptyStateInTableBody ? (
            <tbody>
              <tr>
                <td
                  className="p-2 border border-[#dddddd"
                  colSpan={emptyColSpan}
                >
                  {emptyStateInTableBody}
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {table.getRowModel().rows.map((row) => {
                return (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <td
                          className={`p-2 border border-[#dddddd] ${
                            boldColumns?.includes(cell?.column?.id)
                              ? "border-r-2 border-r-[#cccccc]"
                              : ""
                          }`}
                          key={cell.id}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
      </div>
      {hasAdditionalText && (
        <div className="text-[#8e8a8a] my-3">{additionalText}</div>
      )}
      {hasPagination ? (
        <div className="mt-8 flex justify-center overflow-auto">
          <Pagination
            total={table.getPageCount()}
            page={table.getState().pagination.pageIndex}
            isDisabledPrevPage={!table.getCanPreviousPage()}
            isDisabledNextPage={!table.getCanNextPage()}
            onPrevClick={() => table.previousPage()}
            onNextClick={() => table.nextPage()}
            onChange={(nextPage) => table.setPageIndex(nextPage)}
            onLastClick={() => table.setPageIndex(table.getPageCount())}
            onFirstClick={() => table.setPageIndex(0)}
          />
          {hasSave && (
            <div className="flex-none">
              <Modal
                hasCancel={false}
                description="다운로드 되었습니다."
                onSubmit={() => {}}
              >
                <button
                  onClick={onSave}
                  className="px-[10px] py-[5px] border border-[#cccccc] rounded-[4px] text-[#333333]"
                >
                  <div className="flex items-center gap-2">
                    <SaveIcon />
                    엑셀 저장
                  </div>
                </button>
              </Modal>
            </div>
          )}
        </div>
      ) : null}
    </>
  );
};

export default Table;
